/**
 * AskOverlay Component
 *
 * Cmd+K-style command palette for the "Ask" natural-language search
 * feature (Step 6 of that feature's build). One text box, example
 * queries in the empty state, a simulated progressive loading message
 * (the backend is one request/response with no streaming, so this is
 * timed, not driven by real backend events), then results (grounded
 * summary + read-only filter chips + a "Show on Map" button) rendered in
 * the same overlay. Individual listing cards are intentionally not
 * rendered here - the map + ListingsDrawer are the presentation surface
 * for the actual result set, so results just hand off to them via
 * onShowOnMap rather than duplicating a results grid in the overlay.
 * v1 is single-shot - each submission is independent, no conversation
 * state carried between queries.
 *
 * Escape-to-close and overlay mechanics copied from GeographyModal's
 * established pattern (bound handleKeydown class property, .active class
 * fade/slide transition, click-outside-to-close).
 */
import { injectStyles } from './styles.js';
const EXAMPLE_QUERIES = [
    { label: 'Condos under $2M in Kihei', query: 'condos under $2M in Kihei' },
    { label: 'Walk to the beach', query: 'walk to the beach' },
    { label: "What's new today?", query: "what's new today" },
    { label: 'Waterfront houses with a pool', query: 'waterfront houses with a pool' },
    { label: 'Price drops in the last week', query: 'price drops in the last week' },
];
// Simulated progression only - /ask/search is one request/response, the
// frontend has no real signal for "extraction done, now retrieving."
const LOADING_MESSAGES = ['Understanding your question...', 'Searching listings...'];
const LOADING_MESSAGE_DELAY_MS = 1400;
export class AskOverlay {
    options;
    overlay = null;
    state = 'idle';
    lastQuery = '';
    lastResponse = null;
    lastError = null;
    loadingMessageTimer = null;
    loadingMessageIndex = 0;
    constructor(options) {
        this.options = options;
        injectStyles();
    }
    // initialQuery: when opened from the merged search dropdown, the user
    // already typed their question there - skip the idle/example-queries
    // screen and go straight to loading, with the same text shown (editable)
    // in this overlay's own input.
    open(initialQuery) {
        this.state = 'idle';
        this.lastQuery = initialQuery ?? '';
        this.lastResponse = null;
        this.lastError = null;
        this.render();
        this.show();
        if (initialQuery) {
            void this.submit(initialQuery);
        }
    }
    render() {
        this.cleanup();
        this.overlay = document.createElement('div');
        this.overlay.className = 'sm-ask-overlay';
        this.overlay.innerHTML = this.buildHtml();
        this.options.container.appendChild(this.overlay);
        this.attachEventListeners();
        const input = this.overlay.querySelector('.sm-ask-input');
        input?.focus();
    }
    buildHtml() {
        return `
      <div class="sm-ask-modal">
        <div class="sm-ask-input-row">
          <svg class="sm-ask-input-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
            <path d="M12 3l1.6 4.8L18 9l-4.4 1.6L12 15l-1.6-4.4L6 9l4.4-1.2L12 3z"></path>
            <path d="M19 14l0.8 2.2L22 17l-2.2 0.8L19 20l-0.8-2.2L16 17l2.2-0.8L19 14z"></path>
          </svg>
          <input type="text" class="sm-ask-input" placeholder="Ask about Maui real estate..." value="${this.escapeHtml(this.lastQuery)}">
          <button class="sm-ask-close" aria-label="Close" title="Close">&times;</button>
        </div>
        <div class="sm-ask-body">${this.buildBodyHtml()}</div>
      </div>
    `;
    }
    buildBodyHtml() {
        if (this.state === 'loading') {
            return `
        <div class="sm-ask-loading">
          <span class="sm-ask-spinner"></span>
          <span class="sm-ask-loading-text">${LOADING_MESSAGES[0]}</span>
        </div>
      `;
        }
        if (this.state === 'error') {
            return `
        <div class="sm-ask-error">
          ${this.escapeHtml(this.lastError ?? 'Something went wrong.')}
          <button class="sm-ask-retry-btn">Try again</button>
        </div>
        ${this.buildExamplesHtml()}
      `;
        }
        if (this.state === 'clarifying' && this.lastResponse) {
            return `
        <div class="sm-ask-clarifying">${this.escapeHtml(this.lastResponse.filter.clarifyingQuestion ?? '')}</div>
        ${this.buildExamplesHtml()}
      `;
        }
        if (this.state === 'results' && this.lastResponse) {
            return this.buildResultsHtml(this.lastResponse);
        }
        // idle
        return this.buildExamplesHtml();
    }
    buildExamplesHtml() {
        return `
      <div class="sm-ask-examples-label">Try asking</div>
      ${EXAMPLE_QUERIES.map((ex) => `<button class="sm-ask-example-btn" data-query="${this.escapeHtml(ex.query)}">${this.escapeHtml(ex.label)}</button>`).join('')}
    `;
    }
    buildResultsHtml(response) {
        const isNeighborhoods = response.resultType === 'neighborhoods';
        const itemLabel = isNeighborhoods ? 'neighborhood' : 'listing';
        const count = isNeighborhoods ? (response.neighborhoodResults?.length ?? 0) : response.results.length;
        const summaryHtml = response.summary
            ? `<div class="sm-ask-summary">${this.escapeHtml(response.summary)}</div>`
            : '';
        if (count === 0) {
            return `${summaryHtml}${response.summary ? '' : `<div class="sm-ask-empty-note">No matching ${itemLabel}s found.</div>`}`;
        }
        // Filter chips are property-filter-shaped (price/beds/etc.) - meaningless
        // for a neighborhoods-ranking result, which has no such filter applied.
        const chipsHtml = isNeighborhoods ? '' : this.buildChipsHtml(response.filter);
        return `
      ${summaryHtml}
      ${chipsHtml}
      <button class="sm-ask-show-on-map-btn">Show on Map (${count} ${itemLabel}${count === 1 ? '' : 's'})</button>
    `;
    }
    buildChipsHtml(filter) {
        const chips = [];
        if (filter.propertyType && filter.propertyType !== 'any')
            chips.push(this.capitalize(filter.propertyType));
        if (filter.minPrice != null)
            chips.push(`Over ${this.formatPrice(filter.minPrice)}`);
        if (filter.maxPrice != null)
            chips.push(`Under ${this.formatPrice(filter.maxPrice)}`);
        if (filter.minBedrooms != null)
            chips.push(`${filter.minBedrooms}+ bd`);
        if (filter.maxBedrooms != null)
            chips.push(`Up to ${filter.maxBedrooms} bd`);
        if (filter.minBathrooms != null)
            chips.push(`${filter.minBathrooms}+ ba`);
        if (filter.minSqft != null)
            chips.push(`${filter.minSqft.toLocaleString()}+ sqft`);
        if (filter.maxSqft != null)
            chips.push(`Up to ${filter.maxSqft.toLocaleString()} sqft`);
        if (filter.waterfront)
            chips.push('Waterfront');
        if (filter.pool)
            chips.push('Pool');
        if (filter.strEligible)
            chips.push('STR Eligible');
        if (filter.minDaysOnMarket != null)
            chips.push(`${filter.minDaysOnMarket}+ days on market`);
        if (filter.maxDaysOnMarket != null)
            chips.push(`Listed within ${filter.maxDaysOnMarket} days`);
        if (filter.location)
            chips.push(filter.location);
        if (filter.status && filter.status !== 'any')
            chips.push(this.capitalize(filter.status));
        if (filter.freshness)
            chips.push(`Last ${filter.freshness.hours}h`);
        if (filter.listAgentName)
            chips.push(`By ${filter.listAgentName}`);
        if (filter.excludeAgentName)
            chips.push(`Excluding ${filter.excludeAgentName}`);
        if (filter.listOfficeName)
            chips.push(filter.listOfficeName);
        if (filter.view)
            chips.push(`${this.capitalize(filter.view)} View`);
        if (filter.petsAllowed === true)
            chips.push('Pet Friendly');
        if (filter.petsAllowed === false)
            chips.push('No Pets');
        if (filter.maxHoaFee != null)
            chips.push(`HOA under $${filter.maxHoaFee.toLocaleString()}`);
        if (filter.minGarageSpaces != null)
            chips.push(`${filter.minGarageSpaces}+ car garage`);
        if (filter.minYearBuilt != null)
            chips.push(`Built after ${filter.minYearBuilt}`);
        if (filter.maxYearBuilt != null)
            chips.push(`Built before ${filter.maxYearBuilt}`);
        if (filter.maxAnnualTax != null)
            chips.push(`Tax under $${filter.maxAnnualTax.toLocaleString()}`);
        if (filter.distressed)
            chips.push('Foreclosure/Short Sale');
        if (chips.length === 0)
            return '';
        return `<div class="sm-ask-chips">${chips.map((c) => `<span class="sm-ask-chip">${this.escapeHtml(c)}</span>`).join('')}</div>`;
    }
    attachEventListeners() {
        if (!this.overlay)
            return;
        const closeBtn = this.overlay.querySelector('.sm-ask-close');
        closeBtn?.addEventListener('click', () => this.close());
        this.overlay.addEventListener('click', (e) => {
            if (e.target === this.overlay)
                this.close();
        });
        document.addEventListener('keydown', this.handleKeydown);
        const input = this.overlay.querySelector('.sm-ask-input');
        input?.addEventListener('keydown', (e) => {
            if (e.key === 'Enter') {
                const value = input.value.trim();
                if (value)
                    void this.submit(value);
            }
        });
        this.overlay.querySelectorAll('.sm-ask-example-btn').forEach((btn) => {
            btn.addEventListener('click', () => {
                const query = btn.dataset.query ?? '';
                if (input)
                    input.value = query;
                void this.submit(query);
            });
        });
        const retryBtn = this.overlay.querySelector('.sm-ask-retry-btn');
        retryBtn?.addEventListener('click', () => {
            if (this.lastQuery)
                void this.submit(this.lastQuery);
        });
        this.overlay.querySelector('.sm-ask-show-on-map-btn')?.addEventListener('click', () => {
            if (this.lastResponse)
                this.options.onShowOnMap(this.lastResponse);
            this.close();
        });
    }
    async submit(query) {
        this.lastQuery = query;
        this.state = 'loading';
        this.loadingMessageIndex = 0;
        this.updateBody();
        this.startLoadingMessageCycle();
        try {
            const response = await this.options.onAsk(query);
            this.stopLoadingMessageCycle();
            this.lastResponse = response;
            this.state = !response.filter.understood || response.filter.clarifyingQuestion ? 'clarifying' : 'results';
            // "Not understood" reads the same as a clarifying prompt to the user
            // - both mean "I need more to go on," just phrase it plainly when
            // there's no specific clarifyingQuestion text to show.
            if (!response.filter.understood && !response.filter.clarifyingQuestion) {
                response.filter.clarifyingQuestion = "I couldn't tell if that was a real estate question - try rephrasing, or one of the examples below.";
            }
        }
        catch (error) {
            this.stopLoadingMessageCycle();
            this.lastError = error instanceof Error ? error.message : 'Search failed. Please try again.';
            this.state = 'error';
        }
        this.updateBody();
    }
    startLoadingMessageCycle() {
        this.loadingMessageTimer = setTimeout(() => {
            this.loadingMessageIndex = 1;
            const el = this.overlay?.querySelector('.sm-ask-loading-text');
            if (el && LOADING_MESSAGES[this.loadingMessageIndex]) {
                el.textContent = LOADING_MESSAGES[this.loadingMessageIndex];
            }
        }, LOADING_MESSAGE_DELAY_MS);
    }
    stopLoadingMessageCycle() {
        if (this.loadingMessageTimer) {
            clearTimeout(this.loadingMessageTimer);
            this.loadingMessageTimer = null;
        }
    }
    // Re-renders just the body + toggles the mobile full-screen class,
    // preserving the input's current value/focus rather than tearing down
    // the whole overlay (render() would blur the input on every keystroke-
    // driven state change).
    updateBody() {
        if (!this.overlay)
            return;
        const body = this.overlay.querySelector('.sm-ask-body');
        if (body)
            body.innerHTML = this.buildBodyHtml();
        this.overlay.classList.toggle('sm-ask-has-results', this.state === 'results' || this.state === 'clarifying');
        this.attachBodyEventListeners();
    }
    // Subset of attachEventListeners scoped to elements that get replaced by
    // updateBody() - avoids re-binding the input/close/overlay listeners
    // that already exist and would otherwise double-fire.
    attachBodyEventListeners() {
        if (!this.overlay)
            return;
        const input = this.overlay.querySelector('.sm-ask-input');
        this.overlay.querySelectorAll('.sm-ask-example-btn').forEach((btn) => {
            btn.addEventListener('click', () => {
                const query = btn.dataset.query ?? '';
                if (input)
                    input.value = query;
                void this.submit(query);
            });
        });
        const retryBtn = this.overlay.querySelector('.sm-ask-retry-btn');
        retryBtn?.addEventListener('click', () => {
            if (this.lastQuery)
                void this.submit(this.lastQuery);
        });
        this.overlay.querySelector('.sm-ask-show-on-map-btn')?.addEventListener('click', () => {
            if (this.lastResponse)
                this.options.onShowOnMap(this.lastResponse);
            this.close();
        });
    }
    handleKeydown = (e) => {
        if (e.key === 'Escape' && this.overlay?.classList.contains('active')) {
            this.close();
        }
    };
    show() {
        if (this.overlay) {
            void this.overlay.offsetHeight;
            this.overlay.classList.add('active');
            document.body.style.overflow = 'hidden';
        }
    }
    close() {
        if (!this.overlay)
            return;
        this.overlay.classList.remove('active');
        document.body.style.overflow = '';
        this.stopLoadingMessageCycle();
        setTimeout(() => {
            this.cleanup();
        }, 200);
        this.options.onClose?.();
    }
    cleanup() {
        if (this.overlay) {
            this.overlay.remove();
            this.overlay = null;
        }
    }
    destroy() {
        document.removeEventListener('keydown', this.handleKeydown);
        this.stopLoadingMessageCycle();
        this.cleanup();
    }
    formatPrice(amount) {
        if (amount >= 1000000) {
            return `$${(amount / 1000000).toFixed(amount % 1000000 === 0 ? 0 : 1)}M`;
        }
        return `$${Math.round(amount / 1000)}K`;
    }
    capitalize(s) {
        return s.charAt(0).toUpperCase() + s.slice(1);
    }
    escapeHtml(str) {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    }
}
//# sourceMappingURL=AskOverlay.js.map